import { COMISION_PORCENTAJE, DESCUENTO_DEL_REFERIDO_PORCENTAJE } from '@mc/socios'
import { ArrowRight, Gift } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { Link } from 'react-router'

import { useEnlaceDeSocio } from '../api/consultas'
import { clases, Modal } from '../componentes/ui'
import { CajaDeEnlace } from './CajaDeEnlace'

/** Una de las dos cifras del diálogo: el porcentaje grande y qué significa debajo. */
const Cifra = ({ etiqueta, valor, texto, destacada = false }: { destacada?: boolean; etiqueta: string; texto: ReactNode; valor: ReactNode }) => (
  <div className={clases('rounded-caja border p-4', destacada ? 'border-violeta-borde bg-violeta-suave' : 'border-linea bg-papel-2')}>
    <dt className="text-xs font-medium text-tenue">{etiqueta}</dt>
    <dd className="mt-1.5">
      <span className={clases('block font-display text-[28px] leading-none font-semibold tracking-tight tabular-nums', destacada ? 'text-violeta' : 'text-tinta')}>{valor}</span>
      <span className="mt-1.5 block text-[13px] leading-snug text-tinta-2">{texto}</span>
    </dd>
  </div>
)

/**
 * El botón discreto de la cabecera: «Recomienda y gana el 20 %». Abre un diálogo con
 * lo esencial —qué gana, qué se lleva el invitado, el enlace— y el camino a la
 * pantalla entera. Usa la consulta ligera, que no barre comisiones.
 */
export const ChipDeSocio = ({ espacio }: { espacio: number }) => {
  const { data } = useEnlaceDeSocio(espacio)
  const [abierto, setAbierto] = useState(false)
  if (!data || data.socio.estado !== 'activo') return null
  const comision = data.programa.comisionPorcentaje ?? COMISION_PORCENTAJE
  const descuento = data.programa.descuentoDelReferido ?? DESCUENTO_DEL_REFERIDO_PORCENTAJE
  const recurrente = data.socio.recurrente
  return (
    <>
      <button
        className="hidden items-center gap-1.5 rounded-boton border border-linea px-2.5 py-1.5 text-[13px] text-tinta-2 hover:bg-papel-2 hover:text-tinta sm:inline-flex"
        onClick={() => setAbierto(true)}
        title="El programa de socios"
        type="button"
      >
        <Gift className="size-3.5 text-naranja" />
        <span>Recomienda y gana el {comision} %</span>
      </button>
      {abierto && (
        <Modal
          ancho="max-w-lg"
          cerrar={() => setAbierto(false)}
          titulo={
            <span className="flex items-center gap-2.5">
              <span aria-hidden className="flex size-8 shrink-0 items-center justify-center rounded-full bg-naranja-suave text-naranja">
                <Gift className="size-4" />
              </span>
              Recomienda Wixy y gana
            </span>
          }
        >
          <p className="text-sm text-tinta-2">
            Comparte tu enlace con quien necesite una web. Tu invitado ahorra en su primer pago y tú te llevas una comisión{recurrente ? ' de cada pago, mientras siga en Wixy' : ''}.
          </p>
          <dl className="mt-4 grid grid-cols-2 gap-3">
            <Cifra destacada etiqueta="Tú ganas" texto={recurrente ? 'de cada pago' : 'de su primer pago'} valor={`${comision} %`} />
            <Cifra etiqueta="Tu invitado ahorra" texto="en su primer pago" valor={`${descuento} %`} />
          </dl>
          <div className="mt-5">
            <CajaDeEnlace codigo={data.socio.codigo} compacta enlace={data.enlace} />
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-linea pt-4">
            <p className="text-xs text-tenue">
              Tu código: <span className="font-mono font-medium text-tinta">{data.socio.codigo}</span>
            </p>
            <Link className="inline-flex items-center gap-1 text-sm font-medium text-violeta hover:underline" onClick={() => setAbierto(false)} to={`/e/${espacio}/socios?pestana=ganancias`}>
              Ver mis referidos <ArrowRight className="size-3.5" />
            </Link>
          </div>
        </Modal>
      )}
    </>
  )
}
